// Analytics Service API
// LazySEM Frontend - Analytics API integration (GA4 & GSC)

import { authenticatedFetch } from './authenticatedFetch';

// All analytics endpoints go through the Gateway at /api/v1/analytics
// The authenticatedFetch helper automatically prepends the API_BASE (Gateway URL)
const ANALYTICS_PREFIX = '/api/v1/analytics';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// OAuth Types
export interface OAuthStatus {
  connected: boolean;
  google_email?: string;
  scopes?: string[];
  connected_at?: string;
}

export interface OAuthConnectResponse {
  authorization_url: string;
  state: string;
}

export interface OAuthCompleteRequest {
  code: string;
  state: string;
}

export interface OAuthCompleteResponse {
  success: boolean;
  message: string;
  google_email: string;
}

// GA4 Types
export interface GA4Property {
  id: string;
  property_id: string;
  property_name: string;
  is_selected: boolean;
  last_synced_at?: string;
  created_at: string;
}

export interface GA4PropertiesResponse {
  properties: GA4Property[];
  total: number;
}

export interface GA4SyncRequest {
  start_date: string;
  end_date: string;
  dimensions?: string[];
  metrics?: string[];
}

export interface GA4SyncResponse {
  success: boolean;
  message: string;
  property_id: string;
  property_name: string;
  start_date: string;
  end_date: string;
  records_stored: number;
}

export interface GA4MetricsResponse {
  property_id: string;
  property_name: string;
  start_date: string;
  end_date: string;
  total_records: number;
  metrics: Array<{
    date: string;
    dimensions: Record<string, string>;
    metrics: Record<string, string>;
    fetched_at: string;
  }>;
}

// GSC Types
export interface GSCSite {
  id: string;
  site_url: string;
  permission_level: string;
  is_selected: boolean;
  last_synced_at?: string;
  created_at: string;
}

export interface GSCSitesResponse {
  sites: GSCSite[];
  total: number;
}

export interface GSCSyncRequest {
  start_date: string;
  end_date: string;
  dimensions?: string[];
}

export interface GSCSyncResponse {
  success: boolean;
  message: string;
  site_url: string;
  start_date: string;
  end_date: string;
  records_stored: number;
}

export interface GSCMetricsResponse {
  site_url: string;
  start_date: string;
  end_date: string;
  total_records: number;
  metrics: Array<{
    date: string;
    dimensions: Record<string, any>;
    metrics: {
      clicks: number;
      impressions: number;
      ctr: number;
      position: number;
    };
    fetched_at: string;
  }>;
}

// Dashboard Types
export interface DashboardOverviewResponse {
  period: {
    start_date: string;
    end_date: string;
  };
  ga4: {
    total_sessions: number;
    total_users: number;
    total_users_source?: 'ga4_api' | 'daily_sum';
    total_pageviews: number;
  };
  gsc: {
    total_clicks: number;
    total_impressions: number;
    avg_ctr: number;
    avg_position: number;
  };
}

export interface TimeSeriesDataPoint {
  date: string;
  [key: string]: any;
}

export interface TimeSeriesResponse {
  metric_type: 'ga4' | 'gsc';
  start_date: string;
  end_date: string;
  data: TimeSeriesDataPoint[];
}

export interface TopPage {
  page: string;
  pageviews?: number;
  sessions?: number;
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
}

export interface TopPagesResponse {
  source: 'ga4' | 'gsc';
  start_date: string;
  end_date: string;
  pages: TopPage[];
}

export interface TopQuery {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface TopQueriesResponse {
  start_date: string;
  end_date: string;
  queries: TopQuery[];
}

export interface TrafficByCountry {
  country: string;
  sessions?: number;
  users?: number;
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
}

export interface TrafficByCountryResponse {
  source: 'ga4' | 'gsc';
  start_date: string;
  end_date: string;
  countries: TrafficByCountry[];
}

// Insights Types
export type InsightType = 'trend' | 'anomaly' | 'opportunity' | 'warning' | 'recommendation';
export type InsightCategory = 'traffic' | 'seo' | 'content' | 'technical' | 'conversion';
export type InsightSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';

export interface SuggestedAction {
  action: string;
  priority: 'low' | 'medium' | 'high';
  effort: 'low' | 'medium' | 'high';
}

export interface MetricChange {
  before: number;
  after: number;
  change_pct: number;
}

export interface Insight {
  id: string;
  insight_type: InsightType;
  category: InsightCategory;
  severity: InsightSeverity;
  title: string;
  description: string;
  data_context: {
    period: string;
    metric: string;
    current_value: number;
    previous_value: number;
  };
  suggested_actions: SuggestedAction[];
  affected_pages: string[];
  affected_queries: string[];
  metric_changes: Record<string, MetricChange>;
  is_read: boolean;
  is_actionable: boolean;
  generated_at: string;
  expires_at: string;
  created_at: string;
}

export interface InsightsListResponse {
  insights: Insight[];
  total: number;
  unread_count: number;
}

export interface GenerateInsightsRequest {
  property_id?: string | null;
  site_id?: string | null;
  days_back?: number;
  /** Pass true to bypass the 6-hour cache and regenerate fresh insights */
  force?: boolean;
}

// Performance Snapshot Types
export type PeriodType = 'daily' | 'weekly' | 'monthly';

export interface DeviceBreakdown {
  desktop: { sessions: number; users: number };
  mobile: { sessions: number; users: number };
  tablet: { sessions: number; users: number };
}

export interface CountryBreakdown {
  country: string;
  sessions: number;
  users: number;
}

export interface PerformanceSnapshotResponse {
  id: string;
  snapshot_date: string;
  period_type: PeriodType;
  ga4_summary: {
    sessions: number;
    users: number;
    pageviews: number;
    avg_bounce_rate: number;
    avg_session_duration: number;
    conversions: number;
  };
  gsc_summary: {
    clicks: number;
    impressions: number;
    avg_ctr: number;
    avg_position: number;
  };
  top_pages: Array<{
    page: string;
    pageviews: number;
    clicks: number;
    impressions: number;
    position: number;
  }>;
  top_queries: Array<{
    query: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  device_breakdown: DeviceBreakdown;
  country_breakdown: CountryBreakdown[];
  comparison_vs_previous: {
    sessions_change_pct: number;
    clicks_change_pct: number;
    position_change: number;
  };
  created_at: string;
}

// Weekly Summary Types
export interface WeeklySummaryHighlight {
  type: 'positive' | 'negative' | 'neutral';
  text: string;
}

export interface WeeklySummaryResponse {
  summary: string;
  highlights: WeeklySummaryHighlight[];
  period_start: string;
  period_end: string;
  generated_at: string;
}

// ============================================================================
// OAUTH ENDPOINTS
// ============================================================================

export async function getOAuthStatus(): Promise<OAuthStatus> {
  return authenticatedFetch<OAuthStatus>(`${ANALYTICS_PREFIX}/oauth/status`, {
    method: 'GET',
    serviceName: 'Analytics'
  });
}

export async function getOAuthConnectURL(): Promise<OAuthConnectResponse> {
  // Build redirect URI to frontend callback page
  const redirectUri = `${window.location.origin}/analytics/oauth/callback`;

  return authenticatedFetch<OAuthConnectResponse>(
    `${ANALYTICS_PREFIX}/oauth/connect?redirect_uri=${encodeURIComponent(redirectUri)}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

export async function completeOAuth(
  data: OAuthCompleteRequest
): Promise<OAuthCompleteResponse> {
  return authenticatedFetch<OAuthCompleteResponse>(`${ANALYTICS_PREFIX}/oauth/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    serviceName: 'Analytics'
  });
}

export async function disconnectOAuth(): Promise<{ success: boolean; message: string }> {
  return authenticatedFetch<{ success: boolean; message: string }>(`${ANALYTICS_PREFIX}/oauth/disconnect`, {
    method: 'POST',
    serviceName: 'Analytics'
  });
}

// ============================================================================
// GA4 ENDPOINTS
// ============================================================================

export async function getGA4Properties(): Promise<GA4PropertiesResponse> {
  return authenticatedFetch<GA4PropertiesResponse>(`${ANALYTICS_PREFIX}/ga4/properties`, {
    method: 'GET',
    serviceName: 'Analytics'
  });
}

export async function selectGA4Property(
  propertyId: string,
  isSelected: boolean
): Promise<GA4Property> {
  return authenticatedFetch<GA4Property>(`${ANALYTICS_PREFIX}/ga4/properties/${propertyId}/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_selected: isSelected }),
    serviceName: 'Analytics'
  });
}

export async function syncGA4Property(
  propertyId: string,
  data: GA4SyncRequest
): Promise<GA4SyncResponse> {
  return authenticatedFetch<GA4SyncResponse>(`${ANALYTICS_PREFIX}/ga4/properties/${propertyId}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    serviceName: 'Analytics'
  });
}

export async function getGA4Metrics(
  propertyId: string,
  startDate: string,
  endDate: string
): Promise<GA4MetricsResponse> {
  return authenticatedFetch<GA4MetricsResponse>(
    `${ANALYTICS_PREFIX}/ga4/properties/${propertyId}/metrics?start_date=${startDate}&end_date=${endDate}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

// ============================================================================
// GSC ENDPOINTS
// ============================================================================

export async function getGSCSites(): Promise<GSCSitesResponse> {
  return authenticatedFetch<GSCSitesResponse>(`${ANALYTICS_PREFIX}/gsc/sites`, {
    method: 'GET',
    serviceName: 'Analytics'
  });
}

export async function selectGSCSite(
  siteId: string,
  isSelected: boolean
): Promise<GSCSite> {
  return authenticatedFetch<GSCSite>(`${ANALYTICS_PREFIX}/gsc/sites/${siteId}/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_selected: isSelected }),
    serviceName: 'Analytics'
  });
}

export async function syncGSCSite(
  siteId: string,
  data: GSCSyncRequest
): Promise<GSCSyncResponse> {
  return authenticatedFetch<GSCSyncResponse>(`${ANALYTICS_PREFIX}/gsc/sites/${siteId}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    serviceName: 'Analytics'
  });
}

export async function getGSCMetrics(
  siteId: string,
  startDate: string,
  endDate: string
): Promise<GSCMetricsResponse> {
  return authenticatedFetch<GSCMetricsResponse>(
    `${ANALYTICS_PREFIX}/gsc/sites/${siteId}/metrics?start_date=${startDate}&end_date=${endDate}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

// ============================================================================
// DASHBOARD ENDPOINTS
// ============================================================================

export type SelectedDashboardOverviewResponse = Omit<DashboardOverviewResponse, 'ga4' | 'gsc'> & {
  ga4: DashboardOverviewResponse['ga4'] | null;
  gsc: DashboardOverviewResponse['gsc'] | null;
};

export function getDashboardOverview(startDate: string, endDate: string, propertyId: string | undefined,
  siteId: string | undefined, sourceScope: 'selected'): Promise<SelectedDashboardOverviewResponse>;
export function getDashboardOverview(startDate: string, endDate: string, propertyId?: string,
  siteId?: string, sourceScope?: 'legacy' | 'portfolio'): Promise<DashboardOverviewResponse>;
export async function getDashboardOverview(
  startDate: string,
  endDate: string,
  propertyId?: string,
  siteId?: string,
  sourceScope?: 'legacy' | 'selected' | 'portfolio'
): Promise<DashboardOverviewResponse | SelectedDashboardOverviewResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  if (propertyId) params.append('property_id', propertyId);
  if (siteId) params.append('site_id', siteId);
  if (sourceScope) params.append('source_scope', sourceScope);

  return authenticatedFetch<DashboardOverviewResponse>(
    `${ANALYTICS_PREFIX}/dashboard/overview?${params.toString()}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

export async function getTimeSeriesData(
  startDate: string,
  endDate: string,
  metricType: 'ga4' | 'gsc',
  propertyId?: string,
  siteId?: string
): Promise<TimeSeriesResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    metric_type: metricType,
  });
  if (propertyId) params.append('property_id', propertyId);
  if (siteId) params.append('site_id', siteId);

  return authenticatedFetch<TimeSeriesResponse>(
    `${ANALYTICS_PREFIX}/dashboard/time-series?${params.toString()}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

export async function getTopPages(
  startDate: string,
  endDate: string,
  source: 'ga4' | 'gsc',
  limit: number = 10,
  propertyId?: string,
  siteId?: string
): Promise<TopPagesResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    source,
    limit: limit.toString(),
  });
  if (propertyId) params.append('property_id', propertyId);
  if (siteId) params.append('site_id', siteId);

  return authenticatedFetch<TopPagesResponse>(
    `${ANALYTICS_PREFIX}/dashboard/top-pages?${params.toString()}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

export async function getTopQueries(
  startDate: string,
  endDate: string,
  limit: number = 10,
  siteId?: string
): Promise<TopQueriesResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    limit: limit.toString(),
  });
  if (siteId) params.append('site_id', siteId);

  return authenticatedFetch<TopQueriesResponse>(
    `${ANALYTICS_PREFIX}/dashboard/top-queries?${params.toString()}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

export async function getTrafficByCountry(
  startDate: string,
  endDate: string,
  source: 'ga4' | 'gsc',
  limit: number = 10,
  propertyId?: string,
  siteId?: string
): Promise<TrafficByCountryResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    source,
    limit: limit.toString(),
  });
  if (propertyId) params.append('property_id', propertyId);
  if (siteId) params.append('site_id', siteId);

  return authenticatedFetch<TrafficByCountryResponse>(
    `${ANALYTICS_PREFIX}/dashboard/traffic-by-country?${params.toString()}`,
    { method: 'GET', serviceName: 'Analytics' }
  );
}

// ============================================================================
// INSIGHTS ENDPOINTS
// ============================================================================

export async function getInsights(params?: {
  insight_type?: InsightType;
  category?: InsightCategory;
  severity?: InsightSeverity;
  is_read?: boolean;
  property_id?: string;
  site_id?: string;
  limit?: number;
}): Promise<InsightsListResponse> {
  const queryParams = new URLSearchParams();

  if (params?.insight_type) queryParams.append('insight_type', params.insight_type);
  if (params?.category) queryParams.append('category', params.category);
  if (params?.severity) queryParams.append('severity', params.severity);
  if (params?.is_read !== undefined) queryParams.append('is_read', params.is_read.toString());
  if (params?.property_id) queryParams.append('property_id', params.property_id);
  if (params?.site_id) queryParams.append('site_id', params.site_id);
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const url = queryParams.toString()
    ? `${ANALYTICS_PREFIX}/insights?${queryParams.toString()}`
    : `${ANALYTICS_PREFIX}/insights`;

  return authenticatedFetch<InsightsListResponse>(url, {
    method: 'GET',
    serviceName: 'Analytics'
  });
}

export async function markInsightAsRead(insightId: string): Promise<Insight> {
  return authenticatedFetch<Insight>(
    `${ANALYTICS_PREFIX}/insights/${insightId}/read`,
    {
      method: 'POST',
      serviceName: 'Analytics'
    }
  );
}

export async function generateInsights(
  data: GenerateInsightsRequest
): Promise<InsightsListResponse> {
  return authenticatedFetch<InsightsListResponse>(
    `${ANALYTICS_PREFIX}/insights/generate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      serviceName: 'Analytics'
    }
  );
}

// ============================================================================
// PERFORMANCE SNAPSHOT ENDPOINTS
// ============================================================================

export async function getPerformanceSnapshot(params: {
  start_date: string;
  end_date: string;
  property_id?: string;
  site_id?: string;
  period_type?: PeriodType;
}): Promise<PerformanceSnapshotResponse> {
  const queryParams = new URLSearchParams({
    start_date: params.start_date,
    end_date: params.end_date,
  });

  if (params.property_id) queryParams.append('property_id', params.property_id);
  if (params.site_id) queryParams.append('site_id', params.site_id);
  if (params.period_type) queryParams.append('period_type', params.period_type);

  return authenticatedFetch<PerformanceSnapshotResponse>(
    `${ANALYTICS_PREFIX}/performance/snapshot?${queryParams.toString()}`,
    {
      method: 'GET',
      serviceName: 'Analytics'
    }
  );
}

// ============================================================================
// WEEKLY SUMMARY ENDPOINTS
// ============================================================================

export async function getWeeklySummary(params?: {
  property_id?: string;
  site_id?: string;
  /** Pass true to bypass the 24-hour cache and regenerate a fresh LLM summary */
  force?: boolean;
}): Promise<WeeklySummaryResponse> {
  const queryParams = new URLSearchParams();

  if (params?.property_id) queryParams.append('property_id', params.property_id);
  if (params?.site_id) queryParams.append('site_id', params.site_id);
  if (params?.force) queryParams.append('force', 'true');

  const url = queryParams.toString()
    ? `${ANALYTICS_PREFIX}/dashboard/weekly-summary?${queryParams.toString()}`
    : `${ANALYTICS_PREFIX}/dashboard/weekly-summary`;

  return authenticatedFetch<WeeklySummaryResponse>(url, {
    method: 'GET',
    serviceName: 'Analytics'
  });
}

